import { Immutable } from "immer";
import React from "react";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import type { IBaseComponent } from "../../models/formFields";
import { FormHelpText } from "../FormHelpText";
import { FormTooltip } from "../FormTooltip";
import { FormValidationError } from "../FormValidationError";
import { FormWarning } from "../FormWarning";
import {
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_HAS_ERRORS_CLASS_NAME,
} from "../classNames";
import { showValidationErrors } from "../hooks/formUtils";

/**
 * Wrapper component for form fields (replaces AbstractFormComponent render)
 *
 * The literal `form__field*` classes stay alongside the module classes while
 * legacy contextual rules (`.checkout .form__field`, `.form__field--<name>`)
 * still target them (#34958).
 */
export const FormFieldWrapper = (
    props: React.PropsWithChildren<
        Immutable<
            IBaseComponent & {
                name?: string;
            }
        >
    >,
): React.ReactNode => {
    const hasErrors = showValidationErrors(props);
    const classes = concatClassNames([
        FORM_FIELD_CLASS_NAME,
        props.name ? `form__field--${props.name}` : null,
        hasErrors ? FORM_FIELD_HAS_ERRORS_CLASS_NAME : null,
    ]);

    return (
        <div className={classes}>
            <FormTooltip
                tooltipText={props.tooltipText}
                tooltipLink={props.tooltipLink}
                tooltipClass={props.tooltipClass}
                isIconTrigger={props.isIconTrigger}
            />
            {props.children}
            <FormValidationError {...props} id={props.id} />
            <FormHelpText
                id={props.id}
                helpText={props.helpText}
                helpTextClass={props.helpTextClass}
            />
            <FormWarning
                warningTitle={props.warningTitle}
                warningText={props.warningText}
            />
        </div>
    );
};
